/**
 * 分钟 updown 标题分割：
 * - 中文：`比特币涨还是跌：7月17日…` / `…：——7月17日…`；日内 interval 北京时间，ET 日盘 noon→noon
 * - 非中文：保留原标题时间（UTC 后缀）
 */

import { parseMinuteUpdownSlug } from '@/domain/crypto/crypto-minute-slug.js';
import {
  formatUpdownWindowLabel,
  UPDOWN_WINDOW_TIME_ZONE,
} from '@/domain/crypto/crypto-updown-et-time.js';

function resolveIntervalSecFromLabel(interval) {
  const raw = String(interval ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return 300;
  if (raw === '1d' || raw === 'daily') return 86400;
  const hourMatch = raw.match(/^(\d+)h$/i);
  if (hourMatch) {
    const hours = Number(hourMatch[1]);
    if (Number.isFinite(hours) && hours > 0) return Math.max(hours * 3600, 3600);
  }
  const minMatch = raw.match(/^(\d+)m$/i);
  if (minMatch) {
    const mins = Number(minMatch[1]);
    if (Number.isFinite(mins) && mins > 0) return Math.max(mins * 60, 60);
  }
  return 300;
}

/** 中文全角冒号 + 可选破折号（分割点：`：`） */
const CN_COLON_SPLIT_RE = /：\s*[—–\-−]*\s*/;

/** 中文破折号分隔：`比特币涨还是跌——7月17日…` */
const CN_EM_DASH_SPLIT_RE = /\s*[—–]{1,2}\s*/;

/** 英文标题：` - ` 分隔币种与日期 */
const SPACED_HYPHEN_SPLIT_RE = /\s+[-–—]\s+/;

function splitAtMatch(normalized, match) {
  if (!match || match.index == null || match.index <= 0) return null;
  return {
    prefix: normalized.slice(0, match.index).trim(),
    suffix: normalized.slice(match.index + match[0].length).trim(),
  };
}

/**
 * @returns {{ prefix: string, suffix: string }}
 */
export function splitUpdownTitle(title) {
  const normalized = String(title ?? '').trim();
  if (!normalized) return { prefix: '', suffix: '' };

  const spaced = splitAtMatch(normalized, normalized.match(SPACED_HYPHEN_SPLIT_RE));
  if (spaced) return spaced;

  const emDash = splitAtMatch(normalized, normalized.match(CN_EM_DASH_SPLIT_RE));
  if (emDash) return emDash;

  const colon = splitAtMatch(normalized, normalized.match(CN_COLON_SPLIT_RE));
  if (colon) return colon;

  const hyphenIdx = normalized.indexOf('-');
  if (hyphenIdx > 0) {
    return {
      prefix: normalized.slice(0, hyphenIdx).trim(),
      suffix: normalized.slice(hyphenIdx + 1).trim(),
    };
  }

  return { prefix: normalized, suffix: '' };
}

function resolveUpdownIntervalFromSlug(slug = '') {
  const normalized = String(slug ?? '')
    .trim()
    .toLowerCase();
  if (!normalized) return '';
  const parsed = parseMinuteUpdownSlug(normalized);
  if (parsed?.interval) return parsed.interval;
  const seriesMatch = normalized.match(/-updown-(\d+[mhd])$/i);
  if (seriesMatch?.[1]) return seriesMatch[1].toLowerCase();
  if (/-up-or-down$/i.test(normalized) || /-up-or-down-on-/i.test(normalized)) return '1d';
  if (/-\d{1,2}(am|pm)-et$/i.test(normalized)) return '1h';
  return '';
}

/** 标题前段追加周期标签，如 `Bitcoin Up or Down` → `Bitcoin Up or Down 5m` */
export function appendUpdownIntervalLabel(titlePrefix, interval) {
  const base = String(titlePrefix ?? '').trim();
  const label = String(interval ?? '')
    .trim()
    .toLowerCase();
  if (!base || !label) return base;
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`\\b${escaped}$`, 'i').test(base)) return base;
  return `${base} ${label}`;
}

/**
 * 分钟 / 4h / 日盘展示标题：主标题带周期（5m/4h/1d），时间窗为副标题。
 * 中文：按档位重算（显式 roundTs，否则用 slug）；日内北京时间，日盘/小时盘 ET。
 * 其他语言：保留接口原标题时间（UTC 后缀），不根据浏览器时区换算。
 * @param {string} title
 * @param {string} [slug]
 * @param {{ roundTs?: number, endSec?: number, locale?: string }} [options]
 * @returns {{ title: string, subtitle: string }}
 */
export function formatMinuteUpdownDisplayTitle(title, slug = '', options = {}) {
  const { prefix, suffix } = splitUpdownTitle(title);
  const base = prefix || String(title ?? '').trim();
  const interval = resolveUpdownIntervalFromSlug(slug);
  const parsed = parseMinuteUpdownSlug(slug);
  const intervalSec = resolveIntervalSecFromLabel(parsed?.interval || interval || '5m');
  const isDaily = parsed?.kind === 'daily' || parsed?.interval === 'daily';
  const usesEasternTime = isDaily || parsed?.kind === 'hourly';
  const isZhLocale = String(options.locale || '')
    .toLowerCase()
    .startsWith('zh');

  const parsedStartSec = Number(parsed?.roundTs) || 0;
  const parsedEndSec = Number(parsed?.endSec) || 0;
  const explicitRoundTs = Number(options.roundTs) || 0;
  const roundTs = explicitRoundTs || parsedStartSec;
  const endSec =
    Number(options.endSec) ||
    (roundTs === parsedStartSec && parsedEndSec > roundTs ? parsedEndSec : 0) ||
    (roundTs > 0 ? roundTs + intervalSec : 0);

  let subtitle = suffix;
  if (isZhLocale && roundTs > 0 && endSec > roundTs) {
    const fromRound = formatUpdownWindowLabel(roundTs, endSec, options.locale, {
      timeZone: usesEasternTime ? UPDOWN_WINDOW_TIME_ZONE.EASTERN : UPDOWN_WINDOW_TIME_ZONE.BEIJING,
    });
    if (fromRound) subtitle = fromRound;
  } else if (!subtitle && parsed?.kind === 'daily') {
    // 日盘 title 常无时间窗后缀：用 slug 结算日作副标题
    const match = String(slug ?? '')
      .trim()
      .toLowerCase()
      .match(/-up-or-down-on-(.+)$/i);
    if (match?.[1]) {
      subtitle = match[1]
        .split('-')
        .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
        .join(' ');
    }
  }
  if (!interval) {
    return { title: base, subtitle };
  }
  return {
    title: appendUpdownIntervalLabel(base, interval),
    subtitle,
  };
}

/**
 * 交易面板完整标题：`Bitcoin Up or Down - August 3, 2:30PM-2:35PM BJT`
 * （不加 5m 后缀；时间窗与 Hero 同源 roundTs）
 */
export function buildUpdownCanonicalTradeTitle(title, slug = '', options = {}) {
  const { prefix } = splitUpdownTitle(title);
  const parts = formatMinuteUpdownDisplayTitle(title, slug, options);
  const base = String(prefix || parts.title || '')
    .replace(/\s+\d+[mhd]$/i, '')
    .trim();
  const sub = String(parts.subtitle ?? '').trim();
  if (base && sub) return `${base} - ${sub}`;
  return base || sub || String(title ?? '').trim();
}

/** 标题前段：系列 / 相关市场搜索词，如 `比特币涨还是跌` */
export function extractUpdownSeriesSearchKeyword(title) {
  return splitUpdownTitle(title).prefix;
}

/** @deprecated 同时间窗后缀；相关市场已改用前段搜索 */
export function extractUpdownRelatedSearchKeyword(title) {
  return splitUpdownTitle(title).suffix;
}
